/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : HomologationStatusGraph.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React from "react";
import { PieChart, Pie, Cell } from "recharts";
import { Box, Typography } from "@mui/material";

function HomologationStatusGraph({ data = [] }) {
  const colors = ["#007CB0", "#26890D"];

  // Transform to Recharts format
  const chartData = data.slice(1).map((row) => ({
    name: row[0],
    value: row[1],
  }));

  return (
    <Box
      sx={{
        // width: 350,
        flex: 1,
        width: "100%",
        height: 350,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "visible",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "visible",
        }}
      >
        {/* ✅ Use a slightly larger chart and ensure overflow is visible */}
        <PieChart
          width={300}
          height={260}
          accessibilityLayer={false}
          style={{
            overflow: "visible", // <---- important
          }}
        >
          <Pie
            data={chartData}
            activeShape={false}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={90} // reduce slightly to keep labels inside box
            paddingAngle={0}
            stroke="transparent"
            style={{ pointerEvents: "none" }}
            labelLine={false}
            isAnimationActive={false}
            label={({ cx, cy, midAngle, outerRadius, percent }) => {
              const radius = outerRadius + 20; // distance for label
              const x = cx + radius * Math.cos((-midAngle * Math.PI) / 180);
              const y = cy + radius * Math.sin((-midAngle * Math.PI) / 180);

              return (
                <text
                  x={x}
                  y={y}
                  fill="black"
                  textAnchor={x > cx ? "start" : "end"}
                  dominantBaseline="central"
                  fontSize={12}
                >
                  {(percent * 100).toFixed(1)}%
                </text>
              );
            }}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={colors[index % colors.length]}
              />
            ))}
          </Pie>
        </PieChart>
      </Box>

      {/* Custom Legend */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "center",
          mt: 2,
        }}
      >
        {chartData.map((item, idx) => (
          <Box
            key={item.name}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                bgcolor: colors[idx % colors.length],
              }}
            />
            <Typography variant="body2" sx={{ fontSize: "7px" }}>
              {item.name}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default HomologationStatusGraph;
